import React, { useContext, useMemo, useState } from 'react';
import { Alert, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';

import JobsContext from '../context/JobsContext';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import auth from '../lib/auth';
import useAuthUser from '../lib/auth/useAuthUser';
import AuthModal from '../components/AuthModal';
import SettingsModal from '../components/SettingsModal';
import AdBanner from '../components/AdBanner';

export default function ProfileScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const {
    removeAdsEntitled,
    removeAdsPurchasing,
    removeAdsPriceString,
    purchaseRemoveAds,
    restoreRemoveAds,
  } = useContext(JobsContext);

  const user = useAuthUser();
  const [authVisible, setAuthVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);

  const showRemoveAds = Platform.OS !== 'web';

  const withDebug = (msg: string, debug: string | null) =>
    __DEV__ && debug ? `${msg}\n\n[dev] ${debug}` : msg;

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      track('sign_out');
    } catch {
      // Only fails when auth isn't configured — nothing to surface.
    }
  };

  const handlePurchase = async () => {
    const result = await purchaseRemoveAds();
    if (result.outcome === 'purchased') {
      Alert.alert(t('settings.removeAds.successTitle'), t('settings.removeAds.successMessage'));
    } else if (result.outcome === 'failed') {
      Alert.alert(
        t('settings.removeAds.errorTitle'),
        withDebug(t('settings.removeAds.errorMessage'), result.reason ?? null)
      );
    }
  };

  const handleRestore = async () => {
    const result = await restoreRemoveAds();
    if (result.outcome === 'restored') {
      Alert.alert(t('settings.removeAds.successTitle'), t('settings.removeAds.successMessage'));
    } else if (result.outcome === 'empty') {
      Alert.alert(
        t('settings.removeAds.restoreEmptyTitle'),
        t('settings.removeAds.restoreEmptyMessage')
      );
    } else {
      Alert.alert(
        t('settings.removeAds.errorTitle'),
        withDebug(t('settings.removeAds.errorMessage'), result.reason ?? null)
      );
    }
  };

  const LinkRow = ({
    icon,
    label,
    onPress,
  }: {
    icon: keyof typeof MaterialCommunityIcons.glyphMap;
    label: string;
    onPress: () => void;
  }) => (
    <TouchableOpacity style={styles.row} onPress={onPress} activeOpacity={0.85}>
      <MaterialCommunityIcons name={icon} size={20} color={palette.textPrimary} />
      <Text style={styles.rowLabel}>{label}</Text>
      <MaterialCommunityIcons name="chevron-right" size={20} color={palette.textMuted} />
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{t('profile.title')}</Text>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
          {/* Account */}
          <Text style={styles.sectionTitle}>{t('auth.account')}</Text>
          {user ? (
            <View style={styles.accountBox}>
              <View style={styles.accountInfo}>
                <MaterialCommunityIcons name="account-circle" size={28} color={palette.textPrimary} />
                <Text style={styles.accountEmail} numberOfLines={1}>
                  {user.isAnonymous ? t('auth.loggedInAnonymous') : (user.email ?? user.uid)}
                </Text>
              </View>
              <Button mode="text" compact onPress={handleSignOut} textColor={palette.danger}>
                {t('auth.signOut')}
              </Button>
            </View>
          ) : (
            <Button
              mode="contained"
              icon="login"
              onPress={() => setAuthVisible(true)}
              buttonColor={palette.primary}
              textColor="#0F1115"
              style={styles.signInBtn}
              contentStyle={styles.signInContent}
            >
              {t('profile.signInRegister')}
            </Button>
          )}

          {/* Links */}
          <Text style={[styles.sectionTitle, styles.sectionSpacer]}>{t('profile.more')}</Text>
          <LinkRow
            icon="bell-outline"
            label={t('alerts.title')}
            onPress={() => router.push('/alerts')}
          />
          <LinkRow
            icon="heart-outline"
            label={t('tabs.favorites')}
            onPress={() => router.push('/favorites')}
          />
          <LinkRow
            icon="cog-outline"
            label={t('settings.title')}
            onPress={() => setSettingsVisible(true)}
          />

          {/* Remove Ads (native only) — kept last */}
          {showRemoveAds && (
            <>
              <Text style={[styles.sectionTitle, styles.sectionSpacer]}>
                {t('settings.removeAds.title')}
              </Text>
              {removeAdsEntitled ? (
                <View style={styles.purchased}>
                  <MaterialCommunityIcons name="check-circle" size={20} color={palette.appliedText} />
                  <Text style={styles.purchasedText}>{t('settings.removeAds.purchased')}</Text>
                </View>
              ) : (
                <View style={styles.removeAdsActions}>
                  <Button
                    mode="contained"
                    onPress={handlePurchase}
                    disabled={removeAdsPurchasing}
                    loading={removeAdsPurchasing}
                    buttonColor={palette.primary}
                    textColor="#0F1115"
                    style={styles.removeAdsBuy}
                    contentStyle={styles.signInContent}
                  >
                    {t('settings.removeAds.buy', { price: removeAdsPriceString ?? '$4.99' })}
                  </Button>
                  <Button
                    mode="text"
                    onPress={handleRestore}
                    disabled={removeAdsPurchasing}
                    textColor={palette.textPrimary}
                    style={styles.removeAdsRestore}
                  >
                    {t('settings.removeAds.restore')}
                  </Button>
                </View>
              )}
            </>
          )}
        </ScrollView>
        <AdBanner />
      </View>

      <AuthModal visible={authVisible} onDismiss={() => setAuthVisible(false)} />
      <SettingsModal visible={settingsVisible} onDismiss={() => setSettingsVisible(false)} />
    </SafeAreaView>
  );
}

const isWeb = Platform.OS === 'web';

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    container: {
      flex: 1,
      width: '100%',
      maxWidth: isWeb ? 720 : undefined,
      alignSelf: isWeb ? 'center' : undefined,
      backgroundColor: p.canvas,
    },
    header: { paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.sm },
    headerTitle: { color: p.textPrimary, fontSize: 18, fontWeight: '700' },
    scroll: { flex: 1 },
    content: { padding: spacing.lg, paddingBottom: spacing.xxl, gap: spacing.sm },
    sectionTitle: { fontSize: 15, fontWeight: '700', color: p.textPrimary, marginBottom: spacing.sm },
    sectionSpacer: { marginTop: spacing.lg },
    accountBox: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingLeft: spacing.md,
      paddingRight: spacing.xs,
      paddingVertical: spacing.sm,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    accountInfo: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    accountEmail: { color: p.textPrimary, fontSize: 14, fontWeight: '500', flex: 1 },
    signInBtn: { borderRadius: radius.pill },
    signInContent: { minHeight: 46 },
    purchased: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      borderRadius: radius.md,
      backgroundColor: p.appliedBg,
    },
    purchasedText: { flex: 1, color: p.appliedText, fontSize: 14, fontWeight: '600' },
    removeAdsActions: { gap: spacing.xs },
    removeAdsBuy: { borderRadius: radius.pill },
    removeAdsRestore: { alignSelf: 'flex-start' },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    rowLabel: { flex: 1, color: p.textPrimary, fontSize: 14, fontWeight: '500' },
  });
